import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  getAgentsList,
  type GetAgentsParams,
  getAgentsByConversations,
  type GetAgentsByConversationsParams,
} from "@api/services/agents";
import type { Agent, AgentConversationItem } from "@interfaces/api.interface";

export interface AgentsState {
  agents: Agent[];
  total: number;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  agentsByConversation: Record<string, Agent>; 
  loadingByConversation: boolean;
}

const initialState: AgentsState = {
  agents: [],
  total: 0,
  loading: false,
  error: null,
  lastFetched: null,
  agentsByConversation: {},
  loadingByConversation: false,
};

export const fetchAgents = createAsyncThunk(
  "agents/fetchAgents",
  async (params: GetAgentsParams = {}, { rejectWithValue }) => {
    try {
      const { offset = 0, limit = 20 } = params;
      const result = await getAgentsList({ offset, limit });
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch agents"
      );
    }
  }
);

export const fetchAgentsByConversations = createAsyncThunk(
  "agents/fetchAgentsByConversations",
  async (params: GetAgentsByConversationsParams, { rejectWithValue }) => {
    try {
      const result = await getAgentsByConversations(params);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch agents by conversations"
      );
    }
  }
);

const agentsSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    clearAgents: (state) => {
      state.agents = [];
      state.total = 0;
      state.error = null;
      state.lastFetched = null;
    },
    setAgents: (state, action: PayloadAction<Agent[]>) => {
      state.agents = action.payload;
    },
    removeAgentFromConversation: (state, action: PayloadAction<string>) => {
      const externalId = action.payload;
      if (state.agentsByConversation[externalId]) {
        delete state.agentsByConversation[externalId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAgents.fulfilled,
        (state, action: PayloadAction<{ agents: Agent[]; total: number }>) => {
          state.loading = false;
          state.agents = action.payload.agents || [];
          state.total = action.payload.total || 0;
          state.error = null;
          state.lastFetched = Date.now();
        }
      )
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAgentsByConversations.pending, (state) => {
        state.loadingByConversation = true;
      })
      .addCase(
        fetchAgentsByConversations.fulfilled,
        (state, action: PayloadAction<{ total: number; items: AgentConversationItem[] }>) => {
          state.loadingByConversation = false;
          const newMapping: Record<string, Agent> = {};
          const fetchedExternalIds = new Set<string>();
          
          action.payload.items.forEach((item) => {
            if (item.externalId) {
              fetchedExternalIds.add(item.externalId);
              if (item.agent) {
                newMapping[item.externalId] = item.agent;
              }
            }
          });
          const updatedMapping = { ...state.agentsByConversation };
          Object.keys(newMapping).forEach((externalId) => {
            updatedMapping[externalId] = newMapping[externalId];
          });
          fetchedExternalIds.forEach((externalId) => {
            if (!newMapping[externalId]) {
              delete updatedMapping[externalId];
            }
          });
          state.agentsByConversation = updatedMapping;
        }
      )
      .addCase(fetchAgentsByConversations.rejected, (state) => {
        state.loadingByConversation = false;
      });
  },
});

export const { clearAgents, setAgents, removeAgentFromConversation } = agentsSlice.actions;
export default agentsSlice.reducer;
